import React, { FC, useEffect, useState } from "react";
import User from "../../../../../model/User";
import { NetworkStates } from "../../../../../store/mobile/types";
import { Banner, Button, IconWarning, IconException, IconInfoCircle } from "@geotab/zenith";
import { useDutyStatusLogServiceContext } from "../../../../../ui/framework/hooks/DutyStatusLogServiceContext";
import { useSecurityServiceContext } from "../../../../../ui/framework/hooks/SecurityServiceContext";
import { useStoreSelector } from "../../../../../ui/framework/hooks/UseSelector";
import { IWorkflowState } from "../../../../../ui/workflow";
import { dispatcher, DispatcherEvents } from "../../../../../utils/dispatcher";
import { isDriverOnAnyWorkflow, isDriverViewingInspectionMode } from "../../../../../utils/uri";
import { useRouterContext } from "../../../../../ui/framework/hooks/RouterContext";
import { useStoreContext } from "../../../../../ui/framework/hooks/StoreContext";
import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { useUserServiceContext } from "../../../../../ui/framework/hooks/UserServiceContext";
import { getPageState, toWorkflow } from "../helpers/Helper";
import { BannerDetail,
    BannerId,
    getBrowserSupportMessage,
    getConnectionMessage,
    getEldJurisdictionMessage,
    getPCLimitWarningMessage,
    getLogChangeMessage,
    getWorkdayMessage,
    getDrivingStatusMessage
} from "../helpers/BannerHelper";
import { DisplayDetailEnum } from "../helpers/ConnectionHelper";
import { MobileActions } from "../../../../../store/mobile/actions";

interface BannerAlertProps {
    setConnectionMobileSheet: React.Dispatch<React.SetStateAction<boolean>>;
    setDisplayDetail: React.Dispatch<React.SetStateAction<DisplayDetailEnum>>;
    /**
     * Display mode for banner messages.
     * - "singleline": Enforces single-line header layout for topbar (default)
     * - "multiline": Uses Zenith Banner component with multiline support
     */
    displayMode?: "singleline" | "multiline";
}

export const BannerAlert: FC<BannerAlertProps> =
    ({ setConnectionMobileSheet, setDisplayDetail, displayMode = "singleline" }) => {

    const { store } = useStoreContext();
    const { router } = useRouterContext();
    const { userService } = useUserServiceContext();
    const { securityService } = useSecurityServiceContext();
    const { dutyStatusLogService} = useDutyStatusLogServiceContext();
    const { translate } = useTranslationContext();

    const [bannerMessage, setBannerMessage] = useState<BannerDetail[]>([]);
    const [dismissedBanners, setDismissedBanners] = useState<Set<BannerId>>(new Set());
    const [inspectLogChange, setInspectLogChange] = useState(false);
    const [isDvirLogRemoved, setIsDvirLogRemoved] = useState(false);
    const [previousDriving, setPreviousDriving] = useState<boolean | undefined>(store.getState().data.driving);
    const [showNetworkMessage, setShowNetworkMessage] = useState(false);
    const [currentUser, setCurrentUser] = useState<IUser>(new User(userService.getCurrentUser()));

    // Reactively subscribe to store state changes
    const ioxAttached = useStoreSelector(state => state.mobile.ioxAttached);
    const online = useStoreSelector<NetworkStates | undefined>(state => state.mobile.online);
    const deviceCommunicating = useStoreSelector(state => state.mobile.deviceCommunicating);
    const driving = useStoreSelector(state => state.data.driving);
    const eldDiagnostic = useStoreSelector(state => state.mobile.eldDiagnostic);
    const diagnosticViewed = useStoreSelector(state => state.mobile.diagnosticViewed);
    const eldMalfunction = useStoreSelector(state => state.mobile.eldMalfunction);
    const newUpcomingViolation = useStoreSelector(state => state.mobile.newUpcomingViolation);
    const pcLimitWarningMessage = useStoreSelector(state => state.mobile.pcLimitWarningMessage);
    const workday = useStoreSelector(state => state.data.workday);
    const users = useStoreSelector(state => state.activeUsers);
    const pageState = useStoreSelector(state => state.mobile.pageState);
    const userHosLogs = useStoreSelector(state => state.userHosLogs);

    const openDvirPage = () => {
        setInspectLogChange(false);
        setIsDvirLogRemoved(false);
        router.navigate("dvir/main", true);
    };

    const startWorkday = () => {
        let nextPageState: IWorkflowState = {};
        let driver = userService.getDriversSeatUser();
        if (driver && driver.id !== currentUser.id) {
            nextPageState = { coDriver: true };
        }
        toWorkflow(router, false, nextPageState);
    };

    const openHosStatusPage = () => {
        setPreviousDriving(driving);
        let currentPage = Backbone.history.fragment;
        if (currentPage && currentPage.indexOf("hos/tabs/main/main") === -1) {
            router.navigate("hos", true);
        }
    };

    const dismissBannerMessage = (bannerId: BannerId) => {
        setDismissedBanners(prev => new Set(prev).add(bannerId));
        setBannerMessage(bannerMessage.filter(msg => msg.id !== bannerId));
    };

    const dismissViolation = () => {
        if (!getPageState(store).isLocked) {
            router.navigate("hos", true);
        }
        store.dispatch(MobileActions.setNewUpcomingViolation(false));
    };

    const openConnectionDialog = (display: DisplayDetailEnum) => {
        setConnectionMobileSheet(true);
        setDisplayDetail(display);
    };

    const navigateToMalfunctionsPage = () => {
        router.navigate("hos/malfunctions", true);
    };

    const getUrgentMessage = () => {
        let messages = [getBrowserSupportMessage(), getConnectionMessage(store, showNetworkMessage, currentUser.id, openConnectionDialog)];
        if (isDriverOnAnyWorkflow() || isDriverViewingInspectionMode()) {
            // Should not show malfunction/diagnostics bar on workflow, otherwise the user could navigate directly to the malfunctions page and skip it
            setBannerMessage(messages.filter(msg => msg !== undefined) as BannerDetail[]);
            return;
        }
        messages = [...messages,
            ...getEldJurisdictionMessage(store, navigateToMalfunctionsPage, dismissViolation),
            getPCLimitWarningMessage(store, () => dismissBannerMessage(BannerId.PcLimitWarning)),
            getLogChangeMessage(store, inspectLogChange, isDvirLogRemoved, openDvirPage),
            getWorkdayMessage(store, securityService, currentUser.id, startWorkday),
            getDrivingStatusMessage(store, dutyStatusLogService, currentUser.id, previousDriving, setPreviousDriving, openHosStatusPage)
        ];
        setBannerMessage(messages.filter(msg => msg !== undefined && !dismissedBanners.has(msg.id)) as BannerDetail[]);
    };

    const serverLogUpdate = (incId, isRemove = false) => {
        if (window.location.href.indexOf("dvir/inspect,(id:'") !== -1) {
            let currId = window.location.href.split("inspect,(id:'")[1].slice(0, incId !== null ? incId.length : 0);
            if (currId === incId) {
                setInspectLogChange(true);
                setIsDvirLogRemoved(isRemove);
            }
        }
    };

    useEffect(() => {
        setCurrentUser(new User(userService.getCurrentUser()));
    }, [users]);

    useEffect(() => {
        dispatcher.listenTo(dispatcher, DispatcherEvents.DvirLogSet, (logId, isRemove) => {
            serverLogUpdate(logId, isRemove);
        });

        dispatcher.listenTo(dispatcher, DispatcherEvents.HideTopbarMessage, () => {
            setInspectLogChange(false);
            setIsDvirLogRemoved(false);
        });
    }, []);

    useEffect(() => {
        getUrgentMessage();
    }, [driving,
        pageState,
        pcLimitWarningMessage,
        deviceCommunicating,
        ioxAttached,
        inspectLogChange,
        isDvirLogRemoved,
        previousDriving,
        eldDiagnostic,
        diagnosticViewed,
        eldMalfunction,
        showNetworkMessage,
        newUpcomingViolation,
        workday,
        currentUser.id,
        userHosLogs]);

    useEffect(() => {
        if (online !== NetworkStates.ONLINE) {
            setShowNetworkMessage(true);
        } else {
            setShowNetworkMessage(false);
        }
    }, [online]);

    const getBannerIcon = (type?: string) => {
        switch (type) {
            case "error":
                return <IconException size="huge" />;
            case "warning":
                return <IconWarning size="huge" />;
            case "info":
                return <IconInfoCircle size="huge" />;
            default:
                return null;
        }
    };

    const renderMultilineBanner = (message: BannerDetail, index: number) => (
        <Banner icon key={index} header={message.title} size="XL" multiline type={message.type} role="alert" ariaAtomic="true" ariaLive="assertive" action={message.action ? {
            text: translate(message.actionText || ""),
            onClick: message.action
        } : undefined}>
            { translate(message.content) }
        </Banner>
    );

    /**
     * Renders a single-line banner by manually constructing the DOM structure.
     *
     * NOTE: This duplicates Zenith's Banner HTML because the Zenith <Banner> component
     * automatically applies multiline layout at mobile breakpoints, which breaks the
     * topbar layout. We need single-line behavior at ALL screen sizes, so we bypass
     * the Zenith component and manually control the layout.
     */
    const renderSinglelineBanner = (message: BannerDetail, index: number) => {
        const bannerTypeClass = message.type ? `zen-banner--${ message.type }` : "zen-banner--error";
        return (
            <div key={index} className={`zen-banner zen-banner__block ${ bannerTypeClass } zen-banner__block--XL`} role="alert" aria-atomic="true" aria-live="assertive">
                <div className="zen-banner__content">
                    <div className="zen-banner__icon-block">{getBannerIcon(message.type)}</div>
                    <div className="zen-banner__header topbar-notification-header">{message.title}</div>
                    <div className="zen-banner__message" title={translate(message.content)}>{translate(message.content)}</div>
                    {message.action && (
                        <div className="zen-banner__action">
                            <Button className={`zen-button zen-button--tertiary zen-banner__link zen-banner__link--${ message.type || "error" }`} onClick={message.action}>
                                {translate(message.actionText || "")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            { bannerMessage.map((message, index) =>
                displayMode === "multiline" ? renderMultilineBanner(message, index) : renderSinglelineBanner(message, index))
            }
        </>
    );

};